import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef, useEffect } from 'react';
import { KeyboardKey } from '../../../enums/keyboardKey.js';
import { FOCUSABLE_SELECTORS } from '../../../constants/focusableSelectors.js';

const focusableSelectors = FOCUSABLE_SELECTORS.join(', ');
const styles = {
    // `overflow-hidden` makes the container a scroll container so that
    // `overscroll-none` can stop scroll chaining out to the page behind it.
    container: 'top-0 left-0 fixed flex items-center justify-center w-screen h-dvh overflow-hidden overscroll-none',
    backdropOverlay: 'z-100 absolute inset-0 bg-neutral-700 opacity-50',
    // The card owns the scroll: capped to the small viewport height so it always
    // fits on mobile, with `overscroll-contain` keeping the scroll inside it.
    card: 'bg-bg-main border border-card-border-neutral relative z-999 p-space-500 rounded-radius-200 mx-space-500 desktop:max-w-7/10 w-fit max-h-[calc(100svh-2.5rem)] overflow-y-auto overscroll-contain',
    closeButton: 'sticky top-0 z-10 float-right ml-space-200 mb-space-200 cursor-pointer bg-bg-main',
};
/**
 * A reusable `Modal` component that provides a dialog interface with accessibility features.
 *
 * Like the SideDraw a small shim has been added to account for pixel jitter when the scrollbar disappears.
 *
 * @param {ModalProps} props - The properties for the Modal component.
 * @param {boolean} props.isOpen - Determines whether the modal is open or closed.
 * @param {() => void} props.onClose - Callback function triggered when the modal is closed.
 * @param {React.ReactNode} props.children - The content to be displayed inside the modal.
 *
 * @remarks
 * - This component prevents background scrolling when the modal is open.
 * - Content taller than the viewport scrolls within the modal itself, and the
 *   scroll is contained so it never chains out to the page behind.
 * - Implements a focus trap to ensure keyboard navigation remains within the modal.
 * - Supports closing the modal with the Escape key or by clicking the backdrop.
 *
 * @example
 * ```tsx
 * <Modal isOpen={isModalOpen} onClose={handleClose}>
 *   <p>Modal content goes here</p>
 * </Modal>
 * ```
 */
const Modal = ({ isOpen, onClose, children }) => {
    const dialogRef = useRef(null);
    // Prevent background scrolling when modal is open
    useEffect(() => {
        if (!isOpen)
            return;
        //Compensate for scrollbar disappearance when assigning overflow hidden
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        const root = document.documentElement;
        // Locking the root as well as the body is what actually stops mobile
        // browsers from scrolling the page behind the modal - `overflow: hidden` on
        // the body alone is only honoured while the root's overflow stays visible.
        root.style.overflow = 'hidden';
        root.style.overscrollBehavior = 'none';
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = `${scrollbarWidth}px`;
        return () => {
            root.style.overflow = '';
            root.style.overscrollBehavior = '';
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        };
    }, [isOpen]);
    // Handle focus trap and Escape key to close modal
    useEffect(() => {
        if (!isOpen || !dialogRef.current)
            return;
        const dialog = dialogRef.current;
        const handleKeyDown = (e) => {
            if (e.key === KeyboardKey.Escape) {
                onClose();
                return;
            }
            if (e.key === KeyboardKey.Tab) {
                const focusable = Array.from(dialog.querySelectorAll(focusableSelectors));
                if (focusable.length === 0) {
                    e.preventDefault();
                    return;
                }
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                const active = document.activeElement;
                if (e.shiftKey) {
                    if (active === first || !dialog.contains(active)) {
                        e.preventDefault();
                        last.focus();
                    }
                }
                else {
                    if (active === last || !dialog.contains(active)) {
                        e.preventDefault();
                        first.focus();
                    }
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);
    if (!isOpen)
        return null;
    return (jsxs("div", { className: styles.container, children: [jsx("div", { className: styles.backdropOverlay, onClick: onClose, "aria-hidden": "true" }), jsxs("div", { ref: dialogRef, className: styles.card, role: "dialog", "aria-modal": "true", children: [jsx("button", { className: styles.closeButton, onClick: onClose, "aria-label": "Close modal", children: jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", "aria-hidden": "true", children: jsx("path", { d: "M15.1719 2.42188L9.54688 8.04688L15.125 13.625C15.5938 14.0469 15.5938 14.75 15.125 15.1719C14.7031 15.6406 14 15.6406 13.5781 15.1719L7.95312 9.59375L2.375 15.1719C1.95312 15.6406 1.25 15.6406 0.828125 15.1719C0.359375 14.75 0.359375 14.0469 0.828125 13.5781L6.40625 8L0.828125 2.42188C0.359375 2 0.359375 1.29688 0.828125 0.828125C1.25 0.40625 1.95312 0.40625 2.42188 0.828125L8 6.45312L13.5781 0.875C14 0.40625 14.7031 0.40625 15.1719 0.875C15.5938 1.29688 15.5938 2 15.1719 2.42188Z", fill: "#1D1D1D" }) }) }), children] })] }));
};

export { Modal };
